import React, { useEffect, useState } from "react";
import { X, CheckCircle, AlertCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";
import axios from "axios";
const apiUrl = process.env.REACT_APP_API_URL;

const ScheduleWizard = ({ open, onClose, accountData, refresh,setRefresh }) => {
  let accessToken = useSelector((state) => state.accessToken.value);
  const daysToNumber = {
    Mon: 2,
    Tue: 3,
    Wed: 4,
    Thu: 5,
    Fri: 6,
    Sat: 7,
    Sun: 1,
  };
  const numberToDays = {
    2: "Mon",
    3: "Tue",
    4: "Wed",
    5: "Thu",
    6: "Fri",
    7: "Sat",
    1: "Sun",
  };
  let idToken = useSelector((state) => state.idToken.value);
  const decoded = jwtDecode(idToken);
  const [userName, setUserName] = useState(
    `${decoded.given_name} ${decoded.family_name}`
  );
  const theme = createTheme({
    palette: {
      primary: {
        main: "#003366",
      },
    },
    components: {
      MuiInputLabel: {
        styleOverrides: {
          root: {
            fontSize: "0.910rem",
            color: "#808080",
            textTransform: "none",
            letterSpacing: "0.00938rem",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            fontSize: "0.875rem",
            color: "#003366",
            borderRadius: 6,
          },
          select: {
            "&:focus": {
              backgroundColor: "transparent",
            },
            borderRadius: 6,
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            fontSize: "0.775rem",
            color: "#003366",
            borderRadius: 6,
            "&:hover": {
              backgroundColor: "rgba(0, 51, 102, 0.05)",
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 6,
            fontSize: "0.875rem",
          },
          input: {
            borderRadius: 6,
            fontSize: "0.875rem",
            color: "#003366",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: 6,
            },
          },
        },
      },
    },
  });

  const [startEnabled, setStartEnabled] = useState(false);
  const [stopEnabled, setStopEnabled] = useState(false);
  const [startTime, setStartTime] = useState("07:00");
  const [stopTime, setStopTime] = useState("23:00");
  const [timezone, setTimezone] = useState("Asia/Calcutta");
  const [selectedDays, setSelectedDays] = useState([
    "Mon",
    "Tue",
    "Wed",
    "Thu",
    "Fri",
  ]);
  const [rollbackDate, setRollbackDate] = useState("");
  const [disableReason, setDisableReason] = useState("");
  const [formError, setFormError] = useState(""); // For validation error
  useEffect(() => {
    setStartEnabled(
      accountData
        ? accountData.stateStart === "ENABLED"
          ? true
          : false
        : false
    );
    setStopEnabled(
      accountData ? (accountData.stateStop === "ENABLED" ? true : false) : false
    );
    setStartTime(accountData ? accountData.startTime : "");
    setStopTime(accountData ? accountData.stopTime : "");
    setTimezone(accountData ? accountData.timezone : "");
    setSelectedDays(accountData ?accountData.days.map(dayNumber => numberToDays[dayNumber]) : [""]);
  }, [accountData]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState(null);

  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  const timezoneOptions = [
    { value: "Asia/Calcutta", label: "Asia/Calcutta" },
    { value: "Etc/GMT-4", label: "Etc/GMT-4" },
    { value: "SystemV/CST6", label: "SystemV/CST6" },
  ];

  const toggleDay = (day) => {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  };
  function generateCronExpressions(days, startTime, endTime) {
    const parseTime = (time) => {
      const [hours, minutes] = time.split(":").map(Number);
      return { hours, minutes };
    };
    const subtract30Minutes = (hours, minutes) => {
      const totalMinutes = hours * 60 + minutes - 30;
      const adjustedHours = Math.floor(((totalMinutes + 1440) % 1440) / 60);
      const adjustedMinutes = ((totalMinutes + 1440) % 1440) % 60;
      return { hours: adjustedHours, minutes: adjustedMinutes };
    };
    const { hours: startHour, minutes: startMinute } = parseTime(startTime);
    const { hours: endHour, minutes: endMinute } = parseTime(endTime);
    const { hours: halfHourBeforeHour, minutes: halfHourBeforeMinute } =
      subtract30Minutes(startHour, startMinute);

    const daysString = days.join(",");
    const hasWeekend = days.some((day) => day == "1" || day == "7");
    const endCronDays = hasWeekend ? daysString : `${daysString},1,7`;

    const startCron = `${startMinute} ${startHour} ? * ${daysString} *`;
    let endCron = `${endMinute} ${endHour} ? * ${endCronDays} *`;
    const halfHourBeforeCron = `${halfHourBeforeMinute} ${halfHourBeforeHour} ? * ${daysString} *`;
    return {
      startCron,
      endCron,
      halfHourBeforeCron,
    };
  }
  function generateScheduleDescription(
    days,
    startTime,
    endTime,
    startEnabled,
    stopEnabled
  ) {
    if (!startEnabled && !stopEnabled) {
      return "Disabled";
    }
    if ((startEnabled && !stopEnabled) || (!startEnabled && stopEnabled)) {
      return "On demand";
    }
    const numberToDays = {
      0: "Sunday",
      1: "Monday",
      2: "Tuesday",
      3: "Wednesday",
      4: "Thursday",
      5: "Friday",
      6: "Saturday",
    };
    const weekdays = [1, 2, 3, 4, 5];
    const weekend = [0, 6];
    const allDays = [0, 1, 2, 3, 4, 5, 6];
    const zeroIndexedDays = days.map((day) => day - 1);
    const isEveryday = zeroIndexedDays.length === 7 && allDays.every(day => zeroIndexedDays.includes(day));
    const isWeekend =
      zeroIndexedDays.length === 2 &&
      zeroIndexedDays.every((day) => weekend.includes(day)) &&
      zeroIndexedDays.includes(0) &&
      zeroIndexedDays.includes(6);
    const isWeekday =
      zeroIndexedDays.length === 5 &&
      zeroIndexedDays.every((day) => weekdays.includes(day));
    let consecutive = true;
    for (let i = 0; i < zeroIndexedDays.length - 1; i++) {
      if (zeroIndexedDays[i + 1] - zeroIndexedDays[i] !== 1) {
        consecutive = false;
        break;
      }
    }
    const timeDescription = `${startTime} to ${endTime}`;
    if (isEveryday) {
      return `Everyday ${timeDescription}`;
    }
    if (isWeekend) {
      return `Weekend ${timeDescription}`;
    }
    if (isWeekday) {
      return `Weekdays ${timeDescription}`;
    }
    if (consecutive && zeroIndexedDays.length > 1) {
      return `${numberToDays[zeroIndexedDays[0]]}-${
        numberToDays[zeroIndexedDays[zeroIndexedDays.length - 1]]
      } ${timeDescription}`;
    }
    if (zeroIndexedDays.length === 1) {
      return `${numberToDays[zeroIndexedDays[0]]} ${timeDescription}`;
    }
    const dayNames = zeroIndexedDays.map((day) => numberToDays[day]);
    return `${dayNames.join(", ")} ${timeDescription}`;
  }

  const postData = async () => {
    const query = `
 mutation MyMutation($AccountName:String!,$AccountNo:String!,$id:String!,$scheduledescription:String!,$startecscronexpression:String!,$startrdscronexpression:String!,$stateStart:String!,$stateStop:String!,$stopcronexpression:String!,$timezone:String!,$username:String!, $region:String!, $startTime:String, $stopTime:String, $days:[String], $disableReason:String, $rollbackDate:String, $prevScheduledescription:String!, $prevStateStart:String!, $prevStateStop:String!) {
  updateScheduleResource(
    AccountName: $AccountName
    AccountNo: $AccountNo
    id: $id
    scheduledescription: $scheduledescription
    startecscronexpression: $startecscronexpression
    startrdscronexpression: $startrdscronexpression
    stateStart: $stateStart
    stateStop: $stateStop
    stopcronexpression: $stopcronexpression
    timezone: $timezone
    username: $username
    region:$region
    startTime:$startTime
    stopTime: $stopTime
    days:$days
    disableReason: $disableReason
    rollbackDate: $rollbackDate
    prevScheduledescription: $prevScheduledescription
    prevStateStart: $prevStateStart
    prevStateStop: $prevStateStop
  )
}
`;
    const selectedDaysNumber = selectedDays.map((i) => {
      return daysToNumber[i];
    });
    const { startCron, halfHourBeforeCron } = startEnabled
      ? generateCronExpressions(selectedDaysNumber, startTime, stopTime)
      : {
          startCron: "00 7 ? * 2,3,4,5,6 *",
          halfHourBeforeCron: "30 6 ? * 2,3,4,5,6 *",
        };
    const { endCron } = stopEnabled
      ? generateCronExpressions(selectedDaysNumber, startTime, stopTime)
      : {
          endCron: "23 00 ? * 2,3,4,5,6 *",
        };

    const description = generateScheduleDescription(
      selectedDaysNumber,
      startTime,
      stopTime,
      startEnabled,
      stopEnabled
    );
    const variables = {
      AccountName: accountData.AccountName,
      AccountNo: accountData.AccountNo,
      id: accountData.id,
      region: accountData.region,
      scheduledescription: description,
      startecscronexpression: startCron,
      startrdscronexpression: halfHourBeforeCron,
      stateStart: startEnabled ? "ENABLED" : "DISABLED",
      stateStop: stopEnabled ? "ENABLED" : "DISABLED",
      stopcronexpression: endCron,
      timezone: timezone,
      username: userName,
      startTime,
      stopTime,
      days: selectedDaysNumber,
      disableReason: !startEnabled && !stopEnabled ? disableReason : "N/A",
      rollbackDate: !startEnabled && !stopEnabled ? rollbackDate : "N/A",
      prevScheduledescription: accountData.scheduledescription,
      prevStateStart: accountData.stateStart,
      prevStateStop: accountData.stateStop,
    };
    await axios
      .post(
        apiUrl,
        {
          query: query,
          variables: variables,
        },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      )
      .then((response) => {
        if(response.data.errors && response.data.errors[0].errorType== "Unauthorized")
        setError('You do not have permission to perform the task');
        setRefresh(!refresh);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const tomorrowDateString = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split("T")[0];
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormError("");
    // Validation for compulsory fields
    if (!startEnabled && !stopEnabled) {
      if (!rollbackDate || !disableReason) {
        setFormError("Rollback date and reason for disable are required.");
        setIsSubmitting(false);
        return;
      }
      // if (rollbackDate <= todayDateString()) {
      //   setFormError("Rollback date must be greater than today.");
      //   setIsSubmitting(false);
      //   return;
      // }
    }
    try {
      await postData();
      setShowSuccess(true);
      setTimeout(() => {
        onClose();
        setShowSuccess(false);
        setStartEnabled(
          accountData
            ? accountData.stateStart === "ENABLED"
              ? true
              : false
            : false
        );
        setStopEnabled(
          accountData
            ? accountData.stateStop === "ENABLED"
              ? true
              : false
            : false
        );
        setStartTime(accountData ? accountData.startTime : "");
        setStopTime(accountData ? accountData.stopTime : "");
        setTimezone(accountData ? accountData.timezone : "");
        setSelectedDays(accountData ?accountData.days.map(dayNumber => numberToDays[dayNumber]) : [""]);
        setRollbackDate("");
        setDisableReason("");
      }, 2000);
    } catch (error) {
      console.error("Error saving schedule:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (showSuccess) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm">
        <DialogContent>
          <div className="w-full py-3">
            <div className="text-center">
            {error ? (
                <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-1" />
              ) : (
                <CheckCircle className="h-10 w-10 text-green-500 mx-auto mb-1" />
              )}

              <h2 className="text-lg font-semibold text-gray-900">
                {error
                  ? "You do not have permission to update the schedule"
                  : "Schedule Updated Successfully!"}
              </h2>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const handleClose = () => {
    setStartEnabled(
      accountData
        ? accountData.stateStart === "ENABLED"
          ? true
          : false
        : false
    );
    setStopEnabled(
      accountData ? (accountData.stateStop === "ENABLED" ? true : false) : false
    );
    setStartTime(accountData ? accountData.startTime : "");
    setStopTime(accountData ? accountData.stopTime : "");
    setTimezone(accountData ? accountData.timezone : "");
    setSelectedDays(accountData ?accountData.days.map(dayNumber => numberToDays[dayNumber]) : [""]);
    setRollbackDate("");
    setDisableReason("");
    setFormError("");
    onClose();
    setError(null)
  };
  return (
    <ThemeProvider theme={theme}>
      {/* uncomment this if u want to have outside click to close dialog */}
      {/* <Dialog open={open} onClose={onClose} maxWidth="sm"> */}
      <Dialog open={open} maxWidth="sm">
        <DialogContent>
          <div className="p-3">
            <div className="flex justify-between items-center mb-5">
              <h2 className="text-xl font-bold text-[#003366] tracking-tight">
                Modify Schedule for {accountData && `${accountData.AccountName}`}
              </h2>
              <button
                onClick={handleClose}
                className="p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              {/* Use flex for better alignment */}
              <div className="flex flex-row gap-7">
                {/* Left Column */}
                <div className="basis-[70%] min-w-0 flex flex-col gap-5">
                  <div>
                    <label className="text-sm font-medium text-gray-700 block mb-2">
                      Active Days
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {days.map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() => toggleDay(day)}
                          className={`px-2.5 py-1 text-sm rounded ${
                            selectedDays.includes(day)
                              ? "bg-[#003366] text-white"
                              : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {day}
                        </button>
                      ))}
                    </div>
                  </div>

                  <FormControl size="small" fullWidth sx={{ marginBottom: '0.5rem' }}>
                    <InputLabel>Timezone</InputLabel>
                    <Select
                      value={timezone}
                      label="Timezone"
                      onChange={(e) => setTimezone(e.target.value)}
                      // Add inputProps for border radius
                      inputProps={{ style: { borderRadius: 6 } }}
                    >
                      {timezoneOptions.map((tz) => (
                        <MenuItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Resume Schedule Date */}
                  <FormControl size="small" fullWidth sx={{ marginBottom: '0.5rem' }}>
                    <TextField
                      label="Resume Schedule"
                      type="date"
                      value={rollbackDate}
                      onChange={(e) => setRollbackDate(e.target.value)}
                      disabled={startEnabled || stopEnabled}
                      required={!startEnabled && !stopEnabled}
                      size="small"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      inputProps={{
                        min: tomorrowDateString(),
                      }}
                    />
                  </FormControl>

                  {/* Purpose of Disable */}
                  <FormControl size="small" fullWidth sx={{ marginBottom: '0.5rem' }}>
                    <TextField
                      label="Purpose of Disable"
                      type="text"
                      value={disableReason}
                      onChange={(e) => setDisableReason(e.target.value)}
                      disabled={startEnabled || stopEnabled}
                      required={!startEnabled && !stopEnabled}
                      size="small"
                      placeholder="Enter reason"
                    />
                  </FormControl>

                  {formError && (
                    <div className="text-red-500 text-xs mt-1">{formError}</div>
                  )}
                </div>

                {/* Right Column */}
                <div className="basis-[25%] min-w-0 flex flex-col gap-5">
                  <div className="flex flex-col gap-4">
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Start Time
                        </span>
                        <Switch
                          checked={startEnabled}
                          onChange={(e) => setStartEnabled(e.target.checked)}
                          color="primary"
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#003366",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#003366",
                            },
                          }}
                        />
                      </div>
                      <FormControl size="small" fullWidth>
                        <input
                          type="time"
                          disabled={!startEnabled}
                          value={startTime}
                          onChange={(e) => setStartTime(e.target.value)}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#003366] focus:border-[#003366] transition-colors ${!startEnabled ? "text-gray-400 bg-gray-100" : "bg-white"}`}
                        />
                      </FormControl>
                    </div>

                    <div className="bg-gray-50 p-3 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">
                          Stop Time
                        </span>
                        <Switch
                          checked={stopEnabled}
                          onChange={(e) => setStopEnabled(e.target.checked)}
                          color="primary"
                          size="small"
                          sx={{
                            "& .MuiSwitch-switchBase.Mui-checked": {
                              color: "#003366",
                            },
                            "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                              backgroundColor: "#003366",
                            },
                          }}
                        />
                      </div>
                      <FormControl size="small" fullWidth>
                        <input
                          type="time"
                          disabled={!stopEnabled}
                          value={stopTime}
                          onChange={(e) => setStopTime(e.target.value)}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-[#003366] focus:border-[#003366] transition-colors ${!stopEnabled ? "text-gray-400 bg-gray-100" : "bg-white"}`}
                        />
                      </FormControl>
                    </div>
                  </div>
                </div>
              </div>
              {/* Save button at the bottom, spanning both columns */}
              <div className="mt-6">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-3 py-2 bg-[#003366] text-sm font-semibold text-white rounded hover:bg-[#002244] transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Saving..." : "Save Schedule"}
                </button>
                {formError && (
                  <div className="text-red-500 text-xs mt-2">{formError}</div>
                )}
              </div>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </ThemeProvider>
  );
};

export default ScheduleWizard;